import { isValidUrl } from "helpers/helper";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  sendCheckOmgId,
  sendCheckOmgIdExist,
} from "features/common/layouts/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect, useMemo } from "react";
// import styles from "../../assets/scss/ModalBundleDomain.module.scss"; // File sudah dihapus
import { notifyWarning, notifyError } from "components/ToastMessage";

const defaultForm = {
  domainForwarder: null,
  activateLater: false,
  onlineId: "",
  password: "",
  password_confirmation: "",
};

// Memoized default values to prevent unnecessary rerenders
const defaultAvailableOmgId = { status: "idle", error: "", data: null };
const defaultAuth = { status_login: false, data_login: null };
const defaultOmgIdCheckExist = { exist: false, status: "idle" };

const ModalBundleDomain = (props) => {
  const dispatch = useDispatch();
  const { defaultOnlineId } = props;
  
  // Only get error from availableOmgId (username check), not from omgIdCheckExist (email check)
  // Use memoized selector to prevent unnecessary rerenders
  const availableOmgId = useSelector((state) => state.cart?.availableOmgId);
  const { status, error, data } = useMemo(
    () => availableOmgId || defaultAvailableOmgId,
    [availableOmgId]
  );
  
  // Use memoized selector for auth
  const authState = useSelector((state) => state.auth);
  const { status_login: isLogin, data_login: dataLogin } = useMemo(
    () => authState || defaultAuth,
    [authState]
  );
  
  // Use memoized selector for omgIdCheckExist
  const omgIdCheckExistState = useSelector((state) => state.cart?.omgIdCheckExist);
  const { exist: omgIdExist } = useMemo(
    () => omgIdCheckExistState || defaultOmgIdCheckExist,
    [omgIdCheckExistState]
  );

  const [isChecking, setIsChecking] = useState(false);
  
  // Get firstname from data_login if logged in
  const getDefaultOmgId = () => {
    if (isLogin && dataLogin?.client?.firstname) {
      // Use firstname from client data
      return dataLogin.client.firstname.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    }
    if (isLogin && dataLogin?.user?.first_name) {
      // Fallback to user first_name
      return dataLogin.user.first_name.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    }
    // If not logged in, use defaultOnlineId or empty
    return defaultOnlineId || "";
  };

  const [form, setForm] = useState({
    ...defaultForm,
    onlineId: getDefaultOmgId(),
    domainForwarder: defaultForm.domainForwarder || "",
  });

  useEffect(() => {
    setIsChecking(status === "loading");
  }, [status]);

  // Update form when login status or data_login changes
  useEffect(() => {
    if (isLogin && dataLogin) {
      const defaultId = getDefaultOmgId();
      setForm((prevState) => ({
        ...prevState,
        onlineId: defaultId,
      }));
    } else {
      // Not logged in, use defaultOnlineId or empty
      setForm((prevState) => ({
        ...prevState,
        onlineId: defaultOnlineId || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, dataLogin, defaultOnlineId]);

  // Check omg.id email existence ONLY when modal is opened (not during domain search)
  // Use ref to track if check has been done to avoid duplicate calls
  const hasCheckedEmailRef = React.useRef(false);
  const previousShowRef = React.useRef(props.show);

  useEffect(() => {
    // Only proceed if modal is actually shown
    if (!props.show) {
      // Reset ref when modal closes
      if (previousShowRef.current) {
        hasCheckedEmailRef.current = false;
      }
      previousShowRef.current = false;
      return;
    }
    
    // Only check when:
    // 1. Modal is shown AND just opened (was closed before)
    // 2. User is logged in
    // 3. Haven't checked yet for this modal session
    const modalJustOpened = props.show && !previousShowRef.current;
    
    if (modalJustOpened && isLogin && dataLogin && !hasCheckedEmailRef.current) {
      const { user } = dataLogin;
      if (user && user.email) {
        const email = user.email;
        hasCheckedEmailRef.current = true; // Mark as checked
        // Only dispatch if modal is actually shown
        dispatch(sendCheckOmgIdExist(email));
      }
    }
    
    previousShowRef.current = props.show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, isLogin, dataLogin, dispatch]);

  // Only show error toast when modal is open AND error is from username check (user action)
  // Don't show error for email check (which happens automatically) or when modal is closed
  // Use ref to track if we've already shown error for this specific error message
  const shownErrorRef = React.useRef("");

  useEffect(() => {
    // Only show error if:
    // 1. Modal is open
    // 2. Error exists and is not empty
    // 3. Status is error (from availableOmgId - username check)
    // 4. Haven't shown this error before (to avoid duplicate toasts)
    // Note: Email check errors (omgIdCheckExist) are handled silently
    if (
      props.show && 
      error && 
      error.trim() !== "" && 
      status === "error" &&
      error !== shownErrorRef.current
    ) {
      // Only show error for username check (when user clicks Check button)
      // Email check errors are handled silently
      shownErrorRef.current = error; // Mark as shown
      notifyError("an error occured! " + error);
    }
    
    // Reset shown error when modal closes
    if (!props.show) {
      shownErrorRef.current = "";
    }
  }, [error, props.show, status]);

  // REMOVED: Auto-check on typing - only check when user clicks "Check" button

  const handleDomainForwarder = (e) => {
    setForm((prev) => ({
      ...prev,
      domainForwarder: e.target.value,
    }));
  };

  const handleOnlineId = (e) => {
    setForm((prev) => ({
      ...prev,
      onlineId: e.target.value,
    }));
  };

  // Generate random password for omg.id registration
  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (form.domainForwarder && form.domainForwarder !== "Activate Later") {
      if (!isValidUrl(form.domainForwarder)) {
        notifyWarning("Domain forwarder bukan url yang valid.!");
        return;
      }
    }

    if (form.onlineId) {
      if (!/^[0-9A-Za-z.\-_]+$/u.test(form.onlineId)) {
        notifyWarning(
          "Omg.id hanya boleh berisi huruf, angka, strip, garis bawah dan titik.!"
        );
        return;
      }
    }

    if (form.domainForwarder === null) {
      form.domainForwarder = "Activate Later";
    }

    // Generate password once and use for both password and password_confirmation
    const generatedPassword = form.password || generatePassword();
    
    // Prepare form data with omg.id registration info
    const formData = {
      ...form,
      // Generate password if not provided and user is not logged in or doesn't have omg.id account
      password: generatedPassword,
      password_confirmation: form.password_confirmation || generatedPassword, // Always use same password
      // Get name and email from logged in user
      name: isLogin && dataLogin?.client?.firstname 
        ? `${dataLogin.client.firstname} ${dataLogin.client.lastname || ''}`.trim()
        : isLogin && dataLogin?.user?.first_name
        ? `${dataLogin.user.first_name} ${dataLogin.user.last_name || ''}`.trim()
        : form.onlineId || '',
      email: isLogin && dataLogin?.user?.email 
        ? dataLogin.user.email 
        : isLogin && dataLogin?.client?.email
        ? dataLogin.client.email
        : '',
    };

    props.onFormSubmited(formData);
  };

  const handleCheck = (e) => {
    e?.preventDefault();
    
    if (!form.onlineId) {
      notifyWarning("field Omg.id tidak boleh kosong!");
      return;
    }

    // Validate format
    if (!/^[0-9A-Za-z.\-_]+$/u.test(form.onlineId)) {
      notifyWarning(
        "Omg.id hanya boleh berisi huruf, angka, strip, garis bawah dan titik.!"
      );
      return;
    }

    dispatch(sendCheckOmgId(form.onlineId));
  };

  const activateLaterHandler = (e) => {
    form.activateLater = e.target.checked;
    if (form.domainForwarder === null) {
      setForm((prev) => ({
        ...prev,
        domainForwarder: "Activate Later",
      }));
    }
    handleSubmit(null);
  };

  return (
    <>
      <Modal show={props.show} onHide={props.onHide} backdrop="static" centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold">Bundle Domain</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-3">
            <p className="text-muted mb-4">
              Silahkan isi dengan link marketplace atau website yang sudah anda buat
            </p>
            
            <div className="row g-3">
              <div className="col-12">
                <label htmlFor="forward-domain" className="form-label fw-bold d-flex align-items-center">
                Forward Domain
                <OverlayTrigger
                    placement="right"
                  overlay={
                    <Tooltip>
                        Untuk menggunakan fitur domain forwarder, Anda perlu menggunakan nameserver qwords: [dns1.qwords.id, dns2.qwords.id, dnsiix1.qwords.net, dnsiix2.qwords.net]
                    </Tooltip>
                  }
                >
                  <span
                      className="d-inline-block px-2 py-1 ms-2 fw-bold text-white"
                    style={{
                      backgroundColor: "#6c757d",
                      cursor: "pointer",
                      borderRadius: "50%",
                        fontSize: "10px",
                        width: "18px",
                        height: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    i
                  </span>
                </OverlayTrigger>
              </label>
              <input
                type="url"
                className="form-control"
                id="forward-domain"
                placeholder="e.g. https://shopee.com/my_awesome_store"
                onChange={handleDomainForwarder}
                value={form.domainForwarder || ""}
              />
            </div>
              
            {(!isLogin || !omgIdExist) && (
                <div className="col-12">
                  <label htmlFor="online-id" className="form-label fw-bold">
                  Omg.id
                </label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    omg.id/
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    id="online-id"
                    placeholder="e.g. my_awesome_store"
                    onChange={handleOnlineId}
                    value={form.onlineId || ""}
                  />
                  <button
                    className="btn btn-warning"
                    type="button"
                    onClick={handleCheck}
                    disabled={isChecking}
                  >
                    {isChecking ? "Checking..." : "Check"}
                  </button>
                </div>
                
                <div className="mt-2">
                  <small className="text-muted">
                    *) Khusus pengguna baru Qwords.com atau yang belum terdaftar di{" "}
                    <a 
                      href="https://omg.id" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                    >
                      Omg.id
                    </a>
                    .
                  </small>
                </div>
                
                <div className="mt-2">
                  <small
                    className={`fw-bold ${
                      data?.available ? "text-success" : "text-danger"
                    }`}
                  >
                    {isChecking
                        ? "Checking..."
                      : data?.available
                      ? "✓ Available"
                      : "✗ Not Available, try another keyword."
                    }
                  </small>
                </div>
              </div>
            )}
            </div>
          </Modal.Body>
          
          <Modal.Footer className="border-0 pt-0">
            <div className="d-flex flex-column flex-md-row gap-2 w-100">
            <button
              type="button"
                className={`btn flex-fill ${
                  form.activateLater 
                    ? "btn-secondary" 
                    : "btn-outline-secondary"
                }`}
              onClick={activateLaterHandler}
            >
              {form.activateLater ? "Aktifkan Sekarang" : "Aktifkan Nanti"}
            </button>
              <button type="submit" className="btn btn-cta flex-fill">
              Simpan
            </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default ModalBundleDomain;
